import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet, SheetContent, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import {
  ArrowUpRight, Menu,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { getPlatformStats, validateReferralCode, getPublicPlatformStatus } from "@/lib/afritask";
import { formatCompactNaira } from "@/lib/format";
import { BUILD_VERSION } from "@/lib/build-info";

// ────────────────────────────────────────────────────────────────
// data — no emoji anywhere on the landing page
// ────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  { label: "How it works",    href: "#how" },
  { label: "For enterprises", href: "#enterprise" },
  { label: "Task types",      href: "#tasks" },
  { label: "FAQ",             href: "#faq" },
];

const KNOWLEDGE_ITEMS = [
  { n: "01", title: "Voice dialects",     body: "Yoruba, Igbo, Hausa, Pidgin, Swahili, Amharic — spoken naturally, not read from a script." },
  { n: "02", title: "Cultural context",   body: "Proverbs, folklore, history, wedding rituals, market bargaining. The knowledge search engines can't scrape." },
  { n: "03", title: "Food knowledge",     body: "Jollof, egusi, injera, suya — real names, regional preparations, and the arguments behind them." },
  { n: "04", title: "Everyday translations", body: "How people actually text, joke, and argue across 8+ African languages. Formal and street." },
  { n: "05", title: "Modern slang",       body: "Naija Twitter, Kenyan lingo, Ghanaian pidgin. Six months out of date and the model already sounds foreign." },
  { n: "06", title: "Visual context",     body: "Markets, danfos, aso ebi, danfo drivers, and daily life. Framed by locals, not stock." },
];

// bento — sizes intentionally varied
const TASK_TILES = [
  { size: "large", label: "Voice", title: "Record short clips in your language.",
    body: "Read a prompt aloud, tell a story about your grandmother's cooking, or narrate a short scene. Payouts are highest here because native voice data is the hardest to buy." },
  { size: "small", label: "Translation", title: "Translate short lines both ways." },
  { size: "small", label: "Labeling",     title: "Tag photos with what you actually see." },
  { size: "wide",  label: "Rating", title: "Judge whether an AI's answer sounds African.",
    body: "Chatbots trained on Silicon Valley text get the tone wrong. You catch it in one read." },
];

const HOW_STEPS = [
  { n: "01", title: "Enter a round.",
    body: "Every task is a small round with a fixed reward pool. You spend a few Work Points to enter — that keeps quality high and locks out bots." },
  { n: "02", title: "Do the task honestly.",
    body: "Follow the instructions on the screen. Real work in real time. No copy-paste from ChatGPT — the grader detects it and disqualifies you." },
  { n: "03", title: "Get paid.",
    body: "Top scorers are ranked by our grader. Payouts land in your bank via Paystack — clearing same-day in Nigeria today, with more African markets coming." },
];

const ENTERPRISE_OFFERINGS = [
  { title: "Voice datasets",     body: "Naturally-spoken African-language corpora. Quality-graded, license-clean, delivered as WAV + transcript." },
  { title: "Language datasets",  body: "Parallel corpora for machine translation, TTS, and transcription across 8+ African tongues." },
  { title: "Cultural context",   body: "Structured cultural knowledge — food, tradition, slang, folklore — as JSONL for RLHF and eval sets." },
];

const ENTERPRISE_LOGOS = [
  { name: "Costco",  href: "https://www.costco.ca/ca-supplier-diversity.html" },
  { name: "TD Bank", href: "https://www.td.com/ca/en/about-td/who-we-are/diversity-and-inclusion" },
  { name: "Rogers",  href: "https://about.rogers.com/our-impact/" },
  { name: "Shopify", href: "https://www.shopify.com/careers/culture/diversity-and-belonging" },
  { name: "Loblaws", href: "https://www.loblaw.ca/en/dei/" },
  { name: "RBC",     href: "https://www.rbc.com/diversity-inclusion/index.html" },
];

const FAQS = [
  { q: "What is AfriTask AI?",
    a: "A platform where Africans get paid to share their language, culture, and everyday knowledge — data used to train AI models that actually understand African users." },
  { q: "Do I need to pay to join?",
    a: "No. Signup is free." },
  { q: "How does AfriSelection AI pick who gets paid?",
    a: "Every submission is scored 0-100 by our grading AI against the task's ground truth. Only submissions scoring 100/100 earn a payout — anything less isn't selected. We can't train models on less-than-perfect data." },
  { q: "How do I get paid?",
    a: "Payouts go to any African bank via Paystack. Minimum withdrawal is ₦2,000." },
  { q: "What tasks are available?",
    a: "Voice rating, translation, image labeling, audio transcription, and prompt rating — in Yoruba, Igbo, Hausa, Pidgin, Swahili, Amharic, French, English, and more." },
  { q: "How long does a task take?",
    a: "Most tasks take 1-5 minutes." },
  { q: "Can I refer friends?",
    a: "Yes. When your friend signs up with your code and makes their first top-up, you get 10 bonus points. Plus 5% of their earnings for their first 30 days on the platform." },
  { q: "Where is AfriTask AI based?",
    a: "Made in Canada, for Africa. Built by a Canadian team working with African talent." },
  { q: "Are you looking for enterprise partners?",
    a: "Yes. Contact enterprise@afritaskai.ca to license datasets or commission custom cultural data collection." },
  { q: "Is my money safe?",
    a: "Payments go through Paystack — Africa's most trusted payment processor. We don't hold your withdrawals; they go straight to your bank." },
];

const FOOTER_COLS = [
  {
    heading: "Product",
    links: [
      { label: "Open app",     to: "#open-app", isCta: true },
      { label: "How it works", to: "#how" },
      { label: "Task types",   to: "#tasks" },
      { label: "Referrals",    to: "#" }, // TODO: route
    ],
  },
  {
    heading: "Support",
    links: [
      { label: "FAQ",                   to: "#faq" },
      { label: "Contact",               to: "mailto:hello@afritask.ai", external: true },
      { label: "Report an issue",      to: "mailto:hello@afritask.ai?subject=Issue%20report", external: true },
      { label: "Community guidelines",  to: "#" }, // TODO: route
    ],
  },
  {
    heading: "Legal & Company",
    links: [
      { label: "Terms",             to: "/legal/terms" },
      { label: "Privacy",           to: "/legal/privacy" },
      { label: "Payments · Paystack", to: "https://paystack.com/", external: true },
      { label: "About",             to: "#" }, // TODO: route
    ],
  },
];

const fadeUp  = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } };

// ────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [stats, setStats] = useState({
    total_ngn_paid: 0, tasks_completed: 0, active_earners: 0, avg_rating: 4.8, countries_served: 8,
  });
  const [refInfo, setRefInfo] = useState(null);
  const [waitlistMode, setWaitlistMode] = useState(false);

  useEffect(() => {
    sessionStorage.removeItem("afritask.just_logged_out");
    getPlatformStats().then((s) => setStats({ ...s, countries_served: 8 })).catch(() => {});
    getPublicPlatformStatus().then((s) => setWaitlistMode(!!s.waitlist_mode)).catch(() => {});

    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get("ref");
    const stored = localStorage.getItem("afritask.pending_referral_code");
    const code = (codeFromUrl || stored || "").trim().toUpperCase();
    if (codeFromUrl) localStorage.setItem("afritask.pending_referral_code", codeFromUrl.toUpperCase());
    if (code) {
      validateReferralCode(code)
        .then((r) => { if (r.valid) setRefInfo({ code, referrer_username: r.referrer_display_name || r.referrer_username }); else localStorage.removeItem("afritask.pending_referral_code"); })
        .catch(() => {});
    }

    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openApp = () => {
    const token = localStorage.getItem("afritask.session_token");
    navigate(token ? "/app/dashboard" : "/auth/enter-phone");
  };
  const openLogin = () => navigate("/auth/enter-phone");

  return (
    <div className="font-body min-h-screen overflow-x-hidden text-neutral-900" style={{ background: "#FDF9F1" }}>
      {/* Landing-scoped CSS: aurora mesh + chip float + reduced-motion respect. */}
      <style>{`
        @keyframes lp-aurora-a { 0%,100%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(4%,-3%,0) scale(1.08)} }
        @keyframes lp-aurora-b { 0%,100%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(-5%,4%,0) scale(1.1)} }
        @keyframes lp-aurora-c { 0%,100%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(3%,5%,0) scale(0.94)} }
        @keyframes lp-chip-float { 0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)} }
        .lp-aurora{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:0;filter:blur(70px);opacity:.9}
        .lp-aurora>span{position:absolute;display:block;border-radius:9999px}
        .lp-a1{width:520px;height:520px;left:-4%;top:-10%;background:radial-gradient(circle at center,rgba(180,83,9,.14),transparent 65%);animation:lp-aurora-a 28s ease-in-out infinite}
        .lp-a2{width:460px;height:460px;right:-6%;top:12%;background:radial-gradient(circle at center,rgba(217,119,6,.12),transparent 60%);animation:lp-aurora-b 34s ease-in-out infinite}
        .lp-a3{width:380px;height:380px;left:38%;top:55%;background:radial-gradient(circle at center,rgba(253,230,138,.35),transparent 65%);animation:lp-aurora-c 40s ease-in-out infinite}
        .lp-chip{animation:lp-chip-float 4s ease-in-out infinite}
        .lp-chip:hover{transform:translateY(-2px)}
        @media (prefers-reduced-motion: reduce){
          .lp-a1,.lp-a2,.lp-a3,.lp-chip{animation:none !important}
        }
      `}</style>
      {/* Invited-by banner — slim ink band, no gradient, no emoji */}
      {refInfo && (
        <div className="w-full bg-neutral-900 text-neutral-100" data-testid="invited-banner">
          <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-2.5 text-xs sm:text-sm">
            <span className="text-neutral-400">Invited by</span>
            <span className="font-semibold text-amber-300" data-testid="invited-by-username">
              {refInfo.referrer_username}
            </span>
            <span className="hidden text-neutral-400 sm:inline">— you both earn a bonus after signup.</span>
          </div>
        </div>
      )}

      {/* ═════════════════ NAV — hairline, editorial, no accent hover ═════════════════ */}
      <header className={`sticky top-0 z-40 border-b transition-all ${
          scrolled ? "border-neutral-200 bg-[#FDF9F1]/95 backdrop-blur" : "border-transparent bg-[#FDF9F1]/60"}`}>
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Logo to="/" size="md" />
          <div className="hidden items-center gap-7 md:flex">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href}
                className="text-sm text-neutral-700 transition hover:text-neutral-900">
                {l.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={openLogin}
              className="hidden text-sm font-medium text-neutral-700 hover:text-neutral-900 md:inline-flex"
              data-testid="nav-login">
              Log in
            </button>
            <Button onClick={openApp}
              className="rounded-md px-4 py-2 text-sm font-semibold text-white ring-1 ring-black/5 transition hover:brightness-110"
              style={{ backgroundColor: "#B45309" }}
              data-testid="nav-open-app">
              Open app
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="mt-6 flex flex-col gap-3">
                  {NAV_LINKS.map((l) => (
                    <a key={l.label} href={l.href} onClick={() => setMobileOpen(false)}
                      className="rounded-md px-3 py-2 text-sm text-neutral-800 hover:bg-neutral-100">
                      {l.label}
                    </a>
                  ))}
                  <button onClick={() => { setMobileOpen(false); openLogin(); }}
                    className="mt-2 rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-800">
                    Log in
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>

      {/* ═════════════════ HERO — asymmetric split, left-aligned, single filled CTA ═════════════════ */}
      <section className="relative overflow-hidden">
        {/* Feature 1: animated aurora mesh — 3 soft radial blobs, blurred, slow drift. */}
        <div className="lp-aurora" aria-hidden="true">
          <span className="lp-a1" /><span className="lp-a2" /><span className="lp-a3" />
        </div>
        <div className="relative z-[1] mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 pt-14 pb-16 sm:px-6 sm:pt-20 lg:grid-cols-[1.1fr_1fr] lg:gap-16 lg:pt-28 lg:pb-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            {/* eyebrow */}
            <motion.p variants={fadeUp}
              className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">
              Made in Canada · For Africa
            </motion.p>

            <motion.h1 variants={fadeUp}
              className="font-display mt-4 text-5xl leading-[1.02] tracking-tight text-neutral-900 sm:text-6xl md:text-7xl lg:text-[88px]">
              Get paid to share <span style={{ color: "#B45309" }}>African culture.</span>
            </motion.h1>

            {/* Subhead candidates (final live line is B) —
                A) "Answer honestly for a few minutes. Watch it clear to your bank."
                B) "You already speak the language. We turn that into income."   ← LIVE
                C) "Short tasks on your phone. Real pay by the end of the week."
            */}
            <motion.p variants={fadeUp}
              className="mt-5 max-w-lg text-base leading-relaxed text-neutral-700 sm:text-lg">
              You already speak the language. We turn that into income.
            </motion.p>

            {/* CTAs — ONE filled primary, secondary as text-link (no pill, no accent fill) */}
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Button onClick={openApp}
                className="rounded-md px-5 py-3 text-sm font-semibold text-white ring-1 ring-black/5 transition hover:brightness-110"
                style={{ backgroundColor: "#B45309" }}
                data-testid="hero-open-app">
                Open app
              </Button>
              <a href="#how"
                className="group inline-flex items-center gap-1 text-sm font-medium text-neutral-800 hover:text-neutral-900"
                data-testid="hero-how">
                How it works
              </a>
            </motion.div>
            {waitlistMode && (
              <motion.p variants={fadeUp}
                className="mt-3 text-xs text-neutral-500 sm:text-sm"
                data-testid="hero-waitlist-hint">
                Early access — <span className="font-medium text-[#B45309]">join the waitlist</span>.
              </motion.p>
            )}
          </motion.div>

          {/* Editorial visual (right) — no floating emoji chips, no glow */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="relative order-2 lg:order-none">
            <div className="relative overflow-hidden rounded-lg ring-1 ring-neutral-200">
              <img src="/hero-illustration.png"
                alt="Young African adults working with laptops and smartphones."
                className="h-64 w-full object-cover sm:h-80 lg:h-[440px]"
                data-testid="hero-image" />
              {/* Feature 3: floating annotation chips — desktop only, staggered float. */}
              <div className="hidden md:block" aria-hidden="true">
                <span className="lp-chip absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-[#B45309] bg-white px-3 py-1.5 text-xs font-medium text-neutral-900 shadow-[0_6px_20px_rgba(0,0,0,0.08)]"
                  style={{ animationDelay: "0s" }}>
                  <span className="text-[#B45309]">•</span> AI grades every answer
                </span>
                <span className="lp-chip absolute right-4 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-full border border-[#B45309] bg-white px-3 py-1.5 text-xs font-medium text-neutral-900 shadow-[0_6px_20px_rgba(0,0,0,0.08)]"
                  style={{ animationDelay: "1.3s" }}>
                  <span className="text-[#B45309]">•</span> Autosaves as you type
                </span>
                <span className="lp-chip absolute bottom-4 left-8 inline-flex items-center gap-1.5 rounded-full border border-[#B45309] bg-white px-3 py-1.5 text-xs font-medium text-neutral-900 shadow-[0_6px_20px_rgba(0,0,0,0.08)]"
                  style={{ animationDelay: "2.6s" }}>
                  <span className="text-[#B45309]">•</span> ₦ same-day to your bank
                </span>
              </div>
            </div>
            {/* small caption under image, editorial */}
            <p className="mt-3 text-[11px] uppercase tracking-widest text-neutral-500">
              Paid direct to your bank · Paystack · Live in Nigeria today
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═════════════════ TRUST STRIP — full-bleed narrow band, tight padding ═════════════════ */}
      <section className="border-y border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-5 py-7 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:px-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
              Early access · Live stats · Updated {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }).toUpperCase()}
            </p>
          </div>
          <div className="flex flex-col items-start gap-3 sm:items-end">
            <div className="grid w-full grid-cols-2 gap-x-4 gap-y-5 sm:flex sm:w-auto sm:gap-x-10 sm:gap-y-0">
              <StatMini label="Paid" value={<CountUp value={stats.total_ngn_paid} format={formatCompactNaira} />} accent />
              <StatMini label="Tasks completed" value={<CountUp value={stats.tasks_completed} />} />
              <StatMini label="Active earners"  value={<CountUp value={stats.active_earners} />} />
              <StatMini label="Countries"       value={<CountUp value={stats.countries_served} />} />
            </div>
            <p className="text-[11px] leading-relaxed text-neutral-500">
              Numbers refresh in real time from Paystack payout records.
            </p>
          </div>
        </div>
      </section>

      {/* ═════════════════ POSITIONING — narrow prose, offset, breathing ═════════════════ */}
      <section className="border-b border-neutral-200 bg-[#FDF9F1] py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:pl-24">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">
            Train the AI of tomorrow
          </p>
          <h2 className="font-display mt-4 text-3xl leading-tight tracking-tight text-neutral-900 sm:text-4xl md:text-5xl">
            Silicon Valley trained AI on the wrong internet.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-[1.75] text-neutral-700 sm:text-lg">
            The chatbots you use every day were trained mostly in English, mostly in the US, mostly by people who&apos;ve
            never had to explain what jollof means. AfriTask AI pays Africans to fix that — one voice clip, one
            translation, one honest label at a time. Africa teaches the model. The model pays Africa back.
          </p>
        </div>
      </section>

      {/* ═════════════════ WHAT AFRICA GIVES — staggered editorial list, no cards ═════════════════ */}
      <section id="tasks" className="bg-[#F5EEDF] py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-[1fr_2fr]">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">
                Your language · Your culture
              </p>
              <h2 className="font-display mt-3 text-3xl leading-tight tracking-tight sm:text-4xl">
                Six kinds of knowledge Silicon Valley can&apos;t buy.
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-neutral-600">
                Not survey data. Not stock photos. The stuff that lives in kitchens, buses, and voice notes.
              </p>
            </div>
            <motion.ol initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              variants={stagger} className="divide-y divide-neutral-200">
              {KNOWLEDGE_ITEMS.map((it) => (
                <motion.li key={it.n} variants={fadeUp}
                  className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-4 gap-y-0 py-6 sm:gap-x-6">
                  <span className="font-mono text-xs font-semibold tracking-wider text-amber-700">{it.n}</span>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-neutral-900 sm:text-lg">{it.title}</h3>
                    <p className="mt-1.5 break-words text-sm leading-relaxed text-neutral-600 sm:text-base">{it.body}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ol>
          </div>
        </div>
      </section>

      {/* ═════════════════ TASK BENTO — differently-sized tiles ═════════════════ */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">Task types</p>
            <h2 className="font-display mt-3 text-3xl leading-tight tracking-tight sm:text-4xl">
              Different tasks, different weights, different pay.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:grid-rows-[auto_auto] lg:gap-6">
            {/* Large tile */}
            <BentoTile tile={TASK_TILES[0]}
              className="lg:col-span-2 lg:row-span-2 lg:min-h-[340px]" />
            <BentoTile tile={TASK_TILES[1]} />
            <BentoTile tile={TASK_TILES[2]} />
            <BentoTile tile={TASK_TILES[3]} className="sm:col-span-2 lg:col-span-1 lg:col-start-3 lg:row-start-2" wide />
          </div>
        </div>
      </section>

      {/* ═════════════════ HOW YOU GET PAID — offset content-column-2 ═════════════════ */}
      <section id="how" className="border-y border-neutral-200 bg-[#FDF9F1] py-20 sm:py-28">
        <div className="mx-auto grid max-w-6xl grid-cols-12 gap-6 px-4 sm:px-6">
          <div className="col-span-12 md:col-span-4 md:col-start-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">How you get paid</p>
            <h2 className="font-display mt-3 text-3xl leading-tight tracking-tight sm:text-4xl">
              Three steps.
              <br />No middlemen.
            </h2>
          </div>
          <ol className="col-span-12 space-y-8 md:col-span-6 md:col-start-6">
            {HOW_STEPS.map((s) => (
              <li key={s.n}
                className="grid grid-cols-1 gap-y-2 border-t border-neutral-200 pt-6 first:border-t-0 first:pt-0 md:grid-cols-[auto_minmax(0,1fr)] md:gap-x-6 md:gap-y-1">
                <span className="font-mono text-sm font-semibold text-amber-700">{s.n}</span>
                <h3 className="text-lg font-semibold text-neutral-900 sm:text-xl">{s.title}</h3>
                <span className="hidden md:block" />
                <p className="break-words text-sm leading-relaxed text-neutral-600 sm:text-base">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ═════════════════ ENTERPRISE — full-bleed dark ink strip, editorial ═════════════════ */}
      <section id="enterprise" className="bg-neutral-900 text-neutral-100">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-[1.15fr_1fr]">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-400">For enterprises</p>
            <h2 className="font-display mt-3 text-3xl leading-tight tracking-tight sm:text-4xl md:text-5xl">
              Building AI for African users? Start with real African data.
            </h2>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-neutral-300 sm:text-base">
              Voice, language, and cultural datasets — collected ethically, license-clean, delivered to spec.
              Working with African labs, product teams, and researchers who need training data that isn&apos;t
              a translation of English.
            </p>
            <a href="mailto:enterprise@afritaskai.ca"
              className="group mt-6 inline-flex items-center gap-1 text-sm font-semibold text-amber-300 hover:text-amber-200"
              data-testid="enterprise-contact">
              enterprise@afritaskai.ca
              <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
          <ul className="space-y-6">
            {ENTERPRISE_OFFERINGS.map((o) => (
              <li key={o.title} className="border-t border-neutral-800 pt-6 first:border-t-0 first:pt-0">
                <h3 className="text-base font-semibold text-white sm:text-lg">{o.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-neutral-400">{o.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═════════════════ CANADIAN BRANDS THAT CHAMPION DIVERSITY ═════════════════ */}
      <section className="border-y border-neutral-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12">
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
            Canadian brands that champion diversity
          </p>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
            className="mt-6 grid grid-cols-2 items-center gap-x-6 gap-y-5 sm:grid-cols-3 sm:gap-x-8 sm:gap-y-6 md:grid-cols-6">
            {ENTERPRISE_LOGOS.map(({ name, href }) => (
              <motion.a key={name} href={href} target="_blank" rel="noopener noreferrer"
                variants={fadeUp}
                className="group inline-flex items-center justify-center text-center font-display text-base font-semibold tracking-tight text-neutral-400 transition-colors duration-200 hover:text-neutral-900 hover:underline sm:text-lg"
                data-testid={`brand-link-${name.toLowerCase().replace(/\s+/g, "-")}`}>
                {name}
                <ArrowUpRight className="ml-1 inline-block h-3 w-3 opacity-0 transition-opacity duration-200 group-hover:opacity-60"
                  aria-hidden="true" />
              </motion.a>
            ))}
          </motion.div>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-neutral-500">
            AfriTask AI is a Canadian platform. These brands publicly champion diversity and inclusive
            communities across Canada. Click any name to read their commitment. Not affiliated with AfriTask AI.
          </p>
        </div>
      </section>

      {/* ═════════════════ FAQ — narrow prose, hairline dividers, no cards ═════════════════ */}
      <section id="faq" className="bg-[#FDF9F1] py-20 sm:py-28">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">FAQ</p>
          <h2 className="font-display mt-3 text-3xl leading-tight tracking-tight sm:text-4xl">
            Straight answers.
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-neutral-200">
                <AccordionTrigger className="text-left text-base font-medium text-neutral-900 hover:no-underline sm:text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-sm leading-relaxed text-neutral-600 sm:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ═════════════════ FINAL CTA — asymmetric card, ONE filled button ═════════════════ */}
      <section id="open-app" className="border-t border-neutral-200 bg-[#F5EEDF] py-20 sm:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="grid gap-8 rounded-xl border border-neutral-200 bg-white p-8 sm:p-12 lg:grid-cols-[1.4fr_1fr] lg:items-end lg:p-16">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">Ready when you are</p>
              <h2 className="font-display mt-3 text-3xl leading-tight tracking-tight text-neutral-900 sm:text-4xl md:text-5xl">
                Your first payout could clear tonight.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600 sm:text-base">
                Sign up with your phone number. First task is on the house.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <Button onClick={openApp}
                className="rounded-md px-6 py-3 text-sm font-semibold text-white ring-1 ring-black/5 transition hover:brightness-110"
                style={{ backgroundColor: "#B45309" }}
                data-testid="final-open-app">
                Open app
              </Button>
              <a href="#faq"
                className="text-xs font-medium text-neutral-500 underline-offset-4 hover:text-neutral-800 hover:underline">
                Still not sure?
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═════════════════ FOOTER — 4-col + hairline bottom bar, ink background ═════════════════ */}
      <footer className="bg-neutral-900 text-neutral-300">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-16">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-y-12 lg:grid-cols-4">
            {/* Column 1 — Brand */}
            <div>
              <Logo to="/" size="md" variant="light" />
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-neutral-400">
                Africans get paid to teach AI what Silicon Valley doesn&apos;t know.
              </p>
              <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                Made in Canada · For Africa
              </p>
            </div>
            {FOOTER_COLS.map((col) => (
              <div key={col.heading}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-500">
                  {col.heading}
                </p>
                <ul className="mt-4 space-y-2.5 text-sm">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.isCta ? (
                        <button onClick={openApp}
                          className="text-left text-neutral-200 transition hover:text-amber-300">
                          {l.label}
                        </button>
                      ) : l.external || l.to.startsWith("mailto:") || l.to.startsWith("http") ? (
                        <a href={l.to} className="text-neutral-400 transition hover:text-amber-300"
                          {...(l.to.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                          {l.label}
                        </a>
                      ) : l.to.startsWith("#") ? (
                        <a href={l.to} className="text-neutral-400 transition hover:text-amber-300">{l.label}</a>
                      ) : (
                        <Link to={l.to} className="text-neutral-400 transition hover:text-amber-300">{l.label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="mt-14 flex flex-col gap-5 border-t border-neutral-800 pt-6 text-xs text-neutral-500 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
              <span>© 2026 AfriTask AI</span>
              <span>Made in Canada · Live in Nigeria · Expanding across Africa</span>
              <span data-testid="build-version"
                className="inline-block w-fit rounded-md bg-neutral-800 px-2 py-0.5 font-mono text-[10px] text-neutral-400"
                title="Build identifier">{BUILD_VERSION}</span>
            </div>
            <div className="flex items-center gap-5">
              <a href="https://x.com/afritask_ai" target="_blank" rel="noopener noreferrer"
                className="text-neutral-400 transition hover:text-amber-300">X</a>
              <a href="https://instagram.com/afritask.ai" target="_blank" rel="noopener noreferrer"
                className="text-neutral-400 transition hover:text-amber-300">Instagram</a>
              <a href="https://wa.me/2348103366815" target="_blank" rel="noopener noreferrer"
                className="text-neutral-400 transition hover:text-amber-300">WhatsApp</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Building blocks
// ────────────────────────────────────────────────────────────────
function StatMini({ label, value, accent }) {
  return (
    <div className="flex flex-col">
      <span className={`text-lg font-bold tabular-nums sm:text-xl ${accent ? "text-emerald-700" : "text-neutral-900"}`}>
        {value}
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500">{label}</span>
    </div>
  );
}

function BentoTile({ tile, className = "", wide = false }) {
  return (
    <motion.article initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
      className={`flex flex-col justify-between rounded-xl border border-neutral-200 bg-white p-6 ring-1 ring-black/[0.02] transition hover:border-neutral-300 ${wide ? "" : ""} ${className}`}>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-700">{tile.label}</p>
        <h3 className={`mt-3 font-display leading-snug tracking-tight text-neutral-900 ${
          tile.size === "large" ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"}`}>
          {tile.title}
        </h3>
        {tile.body && (
          <p className={`text-neutral-600 ${tile.size === "large" ? "mt-4 text-sm leading-relaxed sm:text-base" : "mt-3 text-sm leading-relaxed"}`}>
            {tile.body}
          </p>
        )}
      </div>
    </motion.article>
  );
}

function CountUp({ value, prefix = "", format, className = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!value) { setN(0); return; }
    const dur = 1200;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <span className={className} data-testid="count-up">
      {format ? format(n) : `${prefix}${n.toLocaleString("en-NG")}`}
    </span>
  );
}
